import { Checkbox, Tag } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Button as ZhButton, Dialog as ZhDialog, Input as ZhInput } from "zhihao-ui-react";

import request from "@/api";

export interface DepartmentSelectorItem {
    id: number | string;
    name?: string;
    main?: number;
}

export interface DepartmentTreeNode {
    id: number | string;
    data?: { name?: string };
    childNodes?: DepartmentTreeNode[];
}

export interface DepartmentTreeResult {
    rootNode?: DepartmentTreeNode[];
}

export interface DepartmentSelectorProps {
    value?: DepartmentSelectorItem[];
    onChange?: (value: DepartmentSelectorItem[]) => void;
    loadDepartmentTree?: () => Promise<DepartmentTreeResult>;
}

/** 复用 Vue `/admin/dept/tree` 接口。 */
export async function loadDepartmentSelectorTree(): Promise<DepartmentTreeResult> {
    const response = await request.service.get<DepartmentTreeResult>("/admin/dept/tree");
    return response.data;
}

function flattenTree(nodes: DepartmentTreeNode[]): DepartmentSelectorItem[] {
    return nodes.flatMap(node => [{ id: node.id, name: node.data?.name }, ...flattenTree(node.childNodes || [])]);
}

/** 组织架构部门选择器，复用 Vue 的多选、主部门和编辑回显规则。 */
export function DepartmentSelector({
    value = [],
    onChange,
    loadDepartmentTree = loadDepartmentSelectorTree
}: DepartmentSelectorProps) {
    const [tree, setTree] = useState<DepartmentTreeNode[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [draftValue, setDraftValue] = useState<DepartmentSelectorItem[]>(value);
    const allDepartments = useMemo(() => flattenTree(tree), [tree]);
    const selectedIds = new Set(draftValue.map(item => String(item.id)));
    const displayValue = value.map(item => `${item.name || item.id}${item.main === 1 ? "（主）" : ""}`).join("；");

    useEffect(() => {
        let disposed = false;
        void loadDepartmentTree()
            .then(result => {
                if (!disposed) setTree(result.rootNode || []);
            })
            .catch(() => {
                if (!disposed) setTree([]);
            });
        return () => {
            disposed = true;
        };
    }, [loadDepartmentTree]);

    const updateSelection = (department: DepartmentSelectorItem, checked: boolean) => {
        const current = new Map(draftValue.map(item => [String(item.id), item]));
        if (checked) current.set(String(department.id), department);
        else current.delete(String(department.id));
        const next = allDepartments
            .filter(item => current.has(String(item.id)))
            .map(item => current.get(String(item.id))!);
        const primaryId = next.find(item => item.main === 1)?.id ?? next[0]?.id;
        setDraftValue(next.map(item => ({ ...item, main: item.id === primaryId ? 1 : 0 })));
    };

    const setPrimary = (id: number | string) => {
        setDraftValue(draftValue.map(item => ({ ...item, main: item.id === id ? 1 : 0 })));
    };

    /** 删除暂存部门并在主部门被移除时把首项设为主部门。 */
    const removeDepartment = (id: number | string) => {
        const next = draftValue.filter(item => item.id !== id);
        if (next.length && !next.some(item => item.main === 1)) next[0] = { ...next[0], main: 1 };
        setDraftValue(next);
    };

    /** 打开部门弹窗时复制表单值，确认前的勾选不直接修改外层 Form。 */
    const openDialog = () => {
        setDraftValue(value);
        setDialogOpen(true);
    };
    const saveDialog = () => {
        onChange?.(draftValue);
        setDialogOpen(false);
    };

    const renderNodes = (nodes: DepartmentTreeNode[]) =>
        nodes.map(node => {
            const department = { id: node.id, name: node.data?.name };
            return (
                <div key={node.id} className="ml-4">
                    <Checkbox
                        aria-label={`选择部门 ${department.name || department.id}`}
                        checked={selectedIds.has(String(department.id))}
                        onChange={event => updateSelection(department, event.target.checked)}
                    >
                        {department.name || department.id}
                    </Checkbox>
                    {node.childNodes?.length ? renderNodes(node.childNodes) : null}
                </div>
            );
        });

    return (
        <div aria-label="department-selector" className="flex flex-col gap-2">
            <div className="flex gap-2">
                <ZhInput aria-label="部门" readOnly value={displayValue} placeholder="请选择部门" />
                <ZhButton onClick={openDialog}>选择部门</ZhButton>
            </div>
            <ZhDialog
                open={dialogOpen}
                title="选择部门"
                width={842}
                onCancel={() => setDialogOpen(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <ZhButton onClick={() => setDialogOpen(false)}>取消</ZhButton>
                        <ZhButton type="primary" onClick={saveDialog}>
                            确认
                        </ZhButton>
                    </div>
                }
            >
                <div className="flex gap-5">
                    <div className="min-w-0 flex-1 rounded border border-slate-200 p-2">{renderNodes(tree)}</div>
                    <div aria-label="已选择部门" className="min-w-0 flex-1">
                        <div>已选择部门</div>
                        {draftValue.map(item => (
                            <Tag key={item.id}>
                                {item.name || item.id}
                                {item.main === 1 ? "（主）" : ""}
                                {item.main !== 1 ? (
                                    <ZhButton type="link" size="small" onClick={() => setPrimary(item.id)}>
                                        设为主部门
                                    </ZhButton>
                                ) : null}
                                <ZhButton
                                    aria-label={`删除部门 ${item.name || item.id}`}
                                    type="link"
                                    size="small"
                                    onClick={() => removeDepartment(item.id)}
                                >
                                    删除
                                </ZhButton>
                            </Tag>
                        ))}
                    </div>
                </div>
            </ZhDialog>
        </div>
    );
}

export default DepartmentSelector;
